import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function normalizeMachine(machine) {
  return {
    code: machine.MachineCode || machine.machineCode || machine.code || "",
    description:
      machine.MachineDescription ||
      machine.machineDescription ||
      machine.description ||
      "",
    group:
      machine.MachineGroupCode ||
      machine.groupCode ||
      machine.group ||
      "OTHER",
  };
}

export default function WorkstationSelect({
  value,
  onChange,
  allocatedMachines = null,
  label = "Workstation",
  allLabel = "All Workstations",
  disabled = false,
}) {
  const [adminMachines, setAdminMachines] = useState([]);
  const shouldLoadAdminMachines = !Array.isArray(allocatedMachines);

  useEffect(() => {
    if (!shouldLoadAdminMachines) return undefined;

    let cancelled = false;

    axios
      .get(`${API_BASE_URL}/admin/machines`)
      .then((res) => {
        if (!cancelled) setAdminMachines(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setAdminMachines([]);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldLoadAdminMachines]);

  const machines = useMemo(() => {
    const source = Array.isArray(allocatedMachines)
      ? allocatedMachines
      : adminMachines;

    return source
      .map(normalizeMachine)
      .filter((machine) => machine.code)
      .sort((a, b) => {
        const groupCompare = String(a.group).localeCompare(String(b.group));
        if (groupCompare !== 0) return groupCompare;
        return String(a.code).localeCompare(String(b.code));
      });
  }, [allocatedMachines, adminMachines]);

  const groupedMachines = useMemo(() => {
    const groups = {};

    machines.forEach((machine) => {
      if (!groups[machine.group]) groups[machine.group] = [];
      groups[machine.group].push(machine);
    });

    return Object.entries(groups);
  }, [machines]);

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem value="">{allLabel}</MenuItem>

        {groupedMachines.flatMap(([group, groupMachines]) => [
          <ListSubheader key={`${group}-header`}>{group}</ListSubheader>,
          ...groupMachines.map((machine) => (
            <MenuItem key={machine.code} value={machine.code}>
              {machine.code} - {machine.description || machine.code}
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
}
